import * as React from 'react'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'

/**
 * Panel - Standardized panel wrapper
 *
 * Thin wrapper around Card for data panels. Inherits border-first Card
 * styling (`border-border/30`, no shadow).
 *
 * @example
 * ```tsx
 * <Panel>
 *   <PanelHeader title="Metrics" />
 *   <PanelContent>Data here</PanelContent>
 * </Panel>
 * ```
 */
interface PanelProps extends React.ComponentProps<typeof Card> {
  /** Optional header content */
  header?: React.ReactNode
  /** Optional footer content */
  footer?: React.ReactNode
}

export const Panel = React.forwardRef<HTMLDivElement, PanelProps>(
  ({ className, header, footer, children, ...props }, ref) => {
    return (
      <Card ref={ref} className={className} {...props}>
        {header && <div className="px-6 pt-6 pb-0">{header}</div>}
        {children && (
          <div className={cn(header ? 'px-6 pt-4' : 'p-6', footer && 'pb-4')}>
            {children}
          </div>
        )}
        {footer && <div className="px-6 pb-6 pt-0">{footer}</div>}
      </Card>
    )
  }
)
Panel.displayName = 'Panel'

/**
 * PanelHeader - Header for Panel component
 */
export function PanelHeader({
  title,
  description,
  action,
  className,
}: {
  /** Node rather than string so headers can carry an inline affordance (e.g. a help tooltip). */
  title?: React.ReactNode
  description?: string
  action?: React.ReactNode
  className?: string
}) {
  if (!title && !description && !action) return null

  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      {(title || description) && (
        <div className="flex flex-col gap-1">
          {title && (
            <h3 className="text-sm font-medium text-foreground">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      )}
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

/**
 * PanelContent - Content wrapper for Panel
 */
export const PanelContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn('w-full', className)}
      {...props}
    />
  )
})
PanelContent.displayName = 'PanelContent'

